import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './checkout.scss';
import FurnitureNavbar from '../../../../components/layout/Nav';
import withAuth from '../../../auth/WithAuth';
import { serverUrlAuth } from '../../../../serverUrl';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import Mpesa from '../../../../assets/mpesa-logo.png';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};
  const selectedItemIds = locationState.selectedItems || [];
  const passedTotal = locationState.total || 0;

  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [selectedCartItems, setSelectedCartItems] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('mpesa'); // Default to mpesa
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    shipping_address: '',
    phone_number: '',
    saveInfo: false,
  });

  // Form errors
  const [errors, setErrors] = useState({});

  // Redirect if no items selected
  useEffect(() => {
    if (!selectedItemIds || selectedItemIds.length === 0) {
      toast.warning('Please select items to checkout');
      navigate('/cart');
    }
  }, [selectedItemIds, navigate]);

  // Fetch cart and user profile on mount
  useEffect(() => {
    if (selectedItemIds?.length) {
      fetchCart();
      fetchUserProfile();
    }
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await serverUrlAuth.get('cart/');
      console.log('Cart response:', response.data);

      const cartData = response.data;
      const cartItemsArray = cartData.items || [];

      // Filter only selected items
      const transformedItems = cartItemsArray
        .filter((item) => selectedItemIds.includes(item.id))
        .map((item) => ({
          id: item.id,
          productId: item.product?.id,
          name: item.product?.name || 'Product',
          price: parseFloat(item.product?.price || 0),
          quantity: item.quantity,
          image:
            item.product?.images?.[0]?.image ||
            item.product?.images?.[0] ||
            '🛒',
          inStock: (item.product?.stock || 0) > 0,
          stockCount: item.product?.stock || 0,
        }));

      console.log('Selected items for checkout:', transformedItems);
      setCartItems(transformedItems);
      setSelectedCartItems(transformedItems);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load selected items');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await serverUrlAuth.get('users/address-details/');
      console.log('User profile:', response.data);
      setUserProfile(response.data);

      // Pre-fill form with last used data if available
      if (response.data.last_shipping_address) {
        setFormData((prev) => ({
          ...prev,
          shipping_address: response.data.last_shipping_address || '',
          phone_number: response.data.last_phone_number || '',
        }));
        // Also pre-fill M-Pesa phone if available
        if (response.data.last_phone_number) {
          setMpesaPhone(response.data.last_phone_number);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Calculate totals based on selected items only
  const subtotal = selectedCartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const shipping = subtotal > 5000 ? 0 : 350;
  const tax = subtotal * 0.16; // 16% VAT
  const total = subtotal + shipping + tax;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shipping_address.trim()) {
      newErrors.shipping_address = 'Shipping address is required';
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else if (
      !/^(\+?\d{1,3}[-.]?)?\d{10}$/.test(
        formData.phone_number.replace(/\s/g, ''),
      )
    ) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleMpesaPayment = async () => {
    if (
      !mpesaPhone ||
      !/^(\+?\d{1,3}[-.]?)?\d{10}$/.test(mpesaPhone.replace(/\s/g, ''))
    ) {
      toast.error('Please enter a valid M-Pesa phone number');
      return;
    }

    setShowMpesaModal(false);
    setProcessingPayment(true);

    try {
      // 1. Initiate M-Pesa STK Push
      const mpesaResponse = await serverUrlAuth.post(
        'projects/mpesa/stk-push/',
        {
          phone_number: mpesaPhone,
          amount: total,
          account_reference: `ORDER-${Date.now()}`,
        },
      );

      console.log('M-Pesa response:', mpesaResponse.data);

      // 2. Show waiting for payment modal or toast
      toast.info(
        'Please check your phone and enter your M-Pesa PIN to complete payment',
      );

      // 3. Poll for payment confirmation (or use webhooks in production)
      const checkPaymentInterval = setInterval(async () => {
        try {
          const statusResponse = await serverUrlAuth.get(
            `projects/mpesa/check-status/${mpesaResponse.data.checkout_request_id}/`,
          );

          if (statusResponse.data.status === 'completed') {
            clearInterval(checkPaymentInterval);

            // 4. Payment successful - now create the order
            await createOrderAfterPayment();
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }, 3000); // Check every 3 seconds

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkPaymentInterval);
        setProcessingPayment(false);
        toast.error('Payment timeout. Please try again.');
      }, 120000);
    } catch (error) {
      console.error('Error initiating M-Pesa payment:', error);
      toast.error('Failed to initiate M-Pesa payment');
      setProcessingPayment(false);
    }
  };

  const createOrderAfterPayment = async () => {
    try {
      // Prepare order data
      const orderData = {
        shipping_address: formData.shipping_address,
        phone_number: formData.phone_number,
        payment_method: paymentMethod,
        payment_status: 'paid',
        items: selectedCartItems.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
      };

      // Create order
      const response = await serverUrlAuth.post(
        'projects/checkout/',
        orderData,
      );

      console.log('Order placed:', response.data);

      // Remove selected items from cart
      for (const item of selectedCartItems) {
        await serverUrlAuth.delete(`cart/remove/${item.id}/`);
      }

      toast.success('Payment successful! Order placed.');

      // Navigate to order confirmation
      navigate('/order-confirmation', {
        state: {
          orderId: response.data.order_id,
          shipping_address: response.data.shipping_address,
          phone_number: response.data.phone_number,
          items: selectedCartItems,
          total: total,
          payment_method: 'M-Pesa',
        },
      });
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(
        'Payment was successful but failed to create order. Please contact support.',
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCardPayment = async () => {
    setProcessingPayment(true);

    try {
      // Integrate with Stripe, PayPal, etc.
      toast.info('Redirecting to payment gateway...');

      // Simulate payment processing
      setTimeout(async () => {
        // After successful payment, create order
        const orderData = {
          shipping_address: formData.shipping_address,
          phone_number: formData.phone_number,
          payment_method: 'card',
          payment_status: 'paid',
        };

        const response = await serverUrlAuth.post(
          'projects/checkout/',
          orderData,
        );

        // Remove items from cart
        for (const item of selectedCartItems) {
          await serverUrlAuth.delete(`cart/remove/${item.id}/`);
        }

        toast.success('Payment successful! Order placed.');
        navigate('/order-confirmation', {
          state: {
            orderId: response.data.order_id,
            items: selectedCartItems,
            total: total,
            payment_method: 'Card',
          },
        });
      }, 2000);
    } catch (error) {
      console.error('Error processing card payment:', error);
      toast.error('Payment failed');
      setProcessingPayment(false);
    }
  };

  const handleBankTransfer = async () => {
    setProcessingPayment(true);

    try {
      // Create order with pending payment status
      const orderData = {
        shipping_address: formData.shipping_address,
        phone_number: formData.phone_number,
        payment_method: 'bank',
        payment_status: 'pending',
      };

      const response = await serverUrlAuth.post(
        'projects/checkout/',
        orderData,
      );

      // Remove items from cart
      for (const item of selectedCartItems) {
        await serverUrlAuth.delete(`cart/remove/${item.id}/`);
      }

      toast.info(
        'Order created. Please complete bank transfer to confirm your order.',
      );
      navigate('/order-confirmation', {
        state: {
          orderId: response.data.order_id,
          items: selectedCartItems,
          total: total,
          payment_method: 'Bank Transfer',
          bank_details: {
            account_name: 'Your Company Name',
            account_number: '1234567890',
            bank_name: 'Equity Bank',
            branch: 'Main Branch',
          },
        },
      });
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
      setProcessingPayment(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedCartItems.length === 0) {
      toast.error('No items selected for checkout');
      return;
    }

    // Based on payment method, take appropriate action
    if (paymentMethod === 'mpesa') {
      setShowMpesaModal(true);
    } else if (paymentMethod === 'card') {
      handleCardPayment();
    } else if (paymentMethod === 'bank') {
      handleBankTransfer();
    }
  };

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="checkout-page">
          <div className="checkout-loading">
            <div className="spinner"></div>
            <p>Loading checkout...</p>
          </div>
        </div>
      </>
    );
  }

  if (selectedCartItems.length === 0) {
    return (
      <>
        <FurnitureNavbar />
        <div className="checkout-page">
          <div className="empty-checkout">
            <div className="empty-icon">📦</div>
            <h2>No items selected</h2>
            <p>
              Please select items from your cart before proceeding to checkout
            </p>
            <Link to="/cart" className="back-to-cart-btn">
              <svg viewBox="0 0 24 24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
              Back to Cart
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FurnitureNavbar />
      <div className="checkout-page">
        <div className="checkout-container">
          {/* Header */}
          <div className="checkout-header">
            <Link to="/cart" className="back-link">
              <svg viewBox="0 0 24 24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
              Back to Cart
            </Link>
            <h1>Checkout</h1>
          </div>

          <div className="checkout-content">
            {/* Left Column - Forms */}
            <div className="checkout-forms">
              {/* Shipping Information */}
              <div className="form-section">
                <h2>Shipping Information</h2>

                <div className="form-group">
                  <label>Shipping Address *</label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    className={errors.shipping_address ? 'error' : ''}
                    placeholder="Enter your full shipping address"
                    rows="4"
                  />
                  {errors.shipping_address && (
                    <span className="error-message">
                      {errors.shipping_address}
                    </span>
                  )}
                  <small className="hint">
                    Include street address, city, state, and postal code
                  </small>
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className={errors.phone_number ? 'error' : ''}
                    placeholder="e.g., 0712 345 678"
                  />
                  {errors.phone_number && (
                    <span className="error-message">{errors.phone_number}</span>
                  )}
                  <small className="hint">
                    We'll use this to contact you about your order
                  </small>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="form-section">
                <h2>Payment Method</h2>
                <div className="payment-methods-grid">
                  <label
                    className={`payment-option ${paymentMethod === 'mpesa' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="mpesa"
                      checked={paymentMethod === 'mpesa'}
                      onChange={() => handlePaymentMethodChange('mpesa')}
                    />
                    <div className="payment-content">
                      <span className="payment-icon">
                        <img
                          className="mpesa-logo"
                          src={Mpesa}
                          alt="mpesa logo"
                        />
                      </span>
                      <div>
                        <strong>M-Pesa</strong>
                        <small>Pay via M-Pesa mobile money</small>
                      </div>
                    </div>
                  </label>

                  <label
                    className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={() => handlePaymentMethodChange('card')}
                    />
                    <div className="payment-content">
                      <span className="payment-icon">💳</span>
                      <div>
                        <strong>Card Payment</strong>
                        <small>Visa, Mastercard, American Express</small>
                      </div>
                    </div>
                  </label>

                  <label
                    className={`payment-option ${paymentMethod === 'bank' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={() => handlePaymentMethodChange('bank')}
                    />
                    <div className="payment-content">
                      <span className="payment-icon">🏦</span>
                      <div>
                        <strong>Bank Transfer</strong>
                        <small>Direct bank transfer</small>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="order-summary">
              <h2>Order Summary</h2>

              <div className="items-list">
                {selectedCartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <div className="item-image">
                      {typeof item.image === 'string' &&
                      item.image.startsWith('http') ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <span>{item.image}</span>
                      )}
                    </div>
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <span className="item-quantity">
                        Qty: {item.quantity}
                      </span>
                    </div>
                    <div className="item-price">
                      KSh {(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-calculations">
                <div className="summary-row">
                  <span>Subtotal ({selectedCartItems.length} items)</span>
                  <span>KSh {subtotal.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0
                      ? 'Free'
                      : `KSh ${shipping.toLocaleString()}`}
                  </span>
                </div>
                <div className="summary-row">
                  <span>Tax (16% VAT)</span>
                  <span>KSh {tax.toLocaleString()}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>KSh {total.toLocaleString()}</span>
                </div>
              </div>

              {/* Dynamic button based on payment method */}
              <button
                className="payment-btn"
                onClick={handleProceedToPayment}
                disabled={processingPayment}
              >
                {processingPayment ? (
                  'Processing...'
                ) : paymentMethod === 'mpesa' ? (
                  <>📱 Pay with M-Pesa (KSh {total.toLocaleString()})</>
                ) : paymentMethod === 'card' ? (
                  <>💳 Pay with Card (KSh {total.toLocaleString()})</>
                ) : (
                  <>🏦 Complete Bank Transfer</>
                )}
              </button>

              <p className="terms-notice">
                By proceeding, you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* M-Pesa Payment Modal */}
      <Modal
        show={showMpesaModal}
        onHide={() => setShowMpesaModal(false)}
        centered
        className="mpesa-modal"
      >
        <Modal.Header closeButton>
          <h2>M-Pesa Payment</h2>
        </Modal.Header>
        <Modal.Body>
          <div className="mpesa-content">
            <div className="mpesa-icon">
              {/* <img src={Mpesa} alt="mpesa logo" /> */}
            </div>
            <p>
              You're about to pay <strong>KSh {total.toLocaleString()}</strong>{' '}
              via M-Pesa
            </p>

            <div className="form-group">
              <label>M-Pesa Phone Number</label>
              <input
                type="tel"
                value={mpesaPhone}
                onChange={(e) => setMpesaPhone(e.target.value)}
                placeholder="e.g., 0712 345 678"
                className="mpesa-input"
              />
              <small>Enter the phone number registered with M-Pesa</small>
            </div>

            <div className="mpesa-steps">
              <p>
                <strong>What happens next?</strong>
              </p>
              <ol>
                <li>You'll receive an STK push on your phone</li>
                <li>Enter your M-Pesa PIN to authorize payment</li>
                <li>Once confirmed, your order will be processed</li>
              </ol>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowMpesaModal(false)}
            disabled={processingPayment}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleMpesaPayment}
            disabled={processingPayment}
          >
            {processingPayment ? 'Processing...' : 'Pay Now'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(CheckoutPage, 'client');
